import React, { useCallback, useEffect, useMemo, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Text,
  Flex,
  Stack,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  LightMode,
  useColorModeValue, Select,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import CameraPlacementsList from "components/CameraPlacements/CameraPlacementsList";
import { TrafficViolationImagesForm } from "views/Pages/TrafficViolation/TrafficViolationImagesForm";
import { useCustomToast } from "components/CustomToast/CustomToast";
import {
  checkErrors,
  hasError,
  validateUf,
  validateVehiclePlate,
  isOnlyLetters,
  getRecentViolationId,
  getMessageRecentViolation
} from "utils/forms-utils";
import { validateAitDate, validateAitTime, handleAitInputChange, toServerDate, formatAitTypeDisplayLabel } from "utils/traffic-violation-utils";
import { trafficViolationService } from "services/trafficViolationService";
import { trafficViolationTypeService } from "services/trafficViolationTypeService";
import { vehicleService } from "services/vehicleService";
import { agentsService } from "services/agentsService";
import { ROUTES } from "constants/routes-path";
import YearMaskedInput from "../../../components/YearInput/YearMaskedInput";
import { ufs } from "../../../utils";
import { configService } from "../../../services/configService";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR, TIME_FORMAT } from "constants/date-time";
import { AuthContext } from "contexts/AuthContext";

const DEFAULT_VIDEO_MONITORING_OBSERVATION = "FISCALIZAÇÃO REALIZADA POR VIDEOMONITORAMENTO.";

const TrafficViolationNew = ({ isVideoMonitoring = false }) => {
  const { t } = useTranslation();
  const [aitAgents, setAitAgents] = useState(undefined);
  const [aitTypes, setAitTypes] = useState(undefined);
  const [formData, setFormData] = useState(undefined);
  const [onlyPlateStateRequired, setOnlyPlateStateRequired] = useState(false);
  const [inputError, setInputError] = useState({});
  const [hasFirstImage, setHasFirstImage] = useState(false);
  const imagesRef = useRef(undefined);
  const observationsTextareaRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const textColor = useColorModeValue("gray.700", "white");
  const { getUserData } = useContext(AuthContext);
  const ids = useMemo(() => ({
    vehiclePlate: "vehicle-plate",
    vehicleOwnerName: "vehicle-owner-name",
    vehicleUfPlate: "vehicle-uf-plate",
    vehicleCategory: "vehicle-category",
    vehicleSpecification: "vehicle-specification",
    vehicleModel: "vehicle-model",
    vehicleColor: "vehicle-color",
    vehicleType: "vehicle-type",
    vehicleAssemblyYear: "vehicle-assembly-year",
    vehicleModelYear: "vehicle-model-year",
    aitAgent: "ait-agent",
    aitType: "ait-type",
    aitDate: "ait-date",
    aitTime: "ait-time",
    vehicleProprietaryMunicipality: "vehicle-proprietary-municipality",
    aitLocation: "ait-location",
    aitReference: "ait-reference",
    aitObservations: "ait-observations",
    aitFirstImage: "ait-first-image",
    aitSecondImage: "ait-second-image",
  }), []);
  const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;
  const isSaveDisabled = isFormInvalid || (isVideoMonitoring && !hasFirstImage);
  const handleImagesChange = useCallback((filesObj) => {
    setHasFirstImage(!!(filesObj?.["ait-image-1"]?.file));
  }, []);

  const cardSpacing = "20px";
  const formSpacing = "30px";
  const translationKey = "traffic-violation";
  const formTranslationKey = `${translationKey}.form`;
  const formDirection = { sm: "column", md: "row" };

  const initFormData = () => {
    return {
      [ids.vehiclePlate]: {
        value: "",
        validations: [
          { required: true },
          { callback: (args) => validateVehiclePlate(args, `${formTranslationKey}.fields.vehicle-plate.label-plate`) }
        ],
      },
      [ids.vehicleOwnerName]: {
        value: "",
        validations: [],
      },
      [ids.vehicleUfPlate]: {
        value: "",
        validations: [{ required: true }, { callback: validateUf }],
      },
      [ids.vehicleCategory]: {
        value: "",
        validations: [],
      },
      [ids.vehicleSpecification]: {
        value: "",
        validations: [],
      },
      [ids.vehicleModel]: {
        value: "",
        validations: [{ required: !onlyPlateStateRequired }],
      },
      [ids.vehicleColor]: {
        value: "",
        validations: [{ required: !onlyPlateStateRequired }],
      },
      [ids.vehicleType]: {
        value: "",
        validations: [],
      },
      [ids.vehicleAssemblyYear]: {
        value: "",
        validations: [],
      },
      [ids.vehicleModelYear]: {
        value: "",
        validations: [],
      },
      [ids.vehicleProprietaryMunicipality]: {
        value: "",
        validations: []
      },
      [ids.aitAgent]: {
        value: 0,
        validations: [{ required: true }],
      },
      [ids.aitType]: {
        value: 0,
        validations: [{ required: true }],
      },
      [ids.aitDate]: {
        value: moment().format(DATE_FORMAT_ISO),
        validations: [
          {
            callback: ({ values }) =>
              validateAitDate(values, ids),
          }
        ]
      },
      [ids.aitTime]: {
        value: moment().format(TIME_FORMAT),
        validations: [
          {
            callback: ({ values }) =>
              validateAitTime(values, ids),
          }
        ]
      },
      [ids.aitLocation]: {
        value: "",
        validations: [{ required: true }],
      },
      [ids.aitReference]: {
        value: "",
        validations: [{ required: true }],
      },
      [ids.aitObservations]: {
        value: isVideoMonitoring ? DEFAULT_VIDEO_MONITORING_OBSERVATION : "",
        validations: [{ required: true }],
      },
    };
  }

  const handleInputChange = (event) => {
    const { id, value } = event.target;

    if (id === ids.aitObservations) {
      const textarea = event.target;
      const newValue = value.toUpperCase();
      pendingSelectionRef.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
      setFormData((prev) => {
        const next = { ...prev, [ids.aitObservations]: { ...prev[ids.aitObservations], value: newValue } };
        setInputError((prevErr) => checkErrors({ formData: next, inputError: prevErr, name: ids.aitObservations }));
        return next;
      });
      return;
    }

    if (id !== ids.vehicleUfPlate || isOnlyLetters(value)) {
      handleAitInputChange(event, ids, formData, setFormData, inputError, setInputError);
    }
  };

  const handleAitDateChange = (value) => {
    const m = moment(value);
    const date = m.isValid() ? m.format("YYYY-MM-DD") : "";

    const updated = {
      ...formData,
      [ids.aitDate]: {
        ...formData[ids.aitDate],
        value: date,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: ids.aitDate })
    );
  };

  const handleAitTimeChange = (value) => {
    const m = moment(value);
    const time = m.isValid() ? m.format(TIME_FORMAT) : "";

    const updated = {
      ...formData,
      [ids.aitTime]: {
        ...formData[ids.aitTime],
        value: time,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: ids.aitTime })
    );
  };

  const redirectToList = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.trafficViolations);
  }

  const handleVehicleSearch = async () => {
    try {
      const result = await vehicleService.findByPlate(formData[ids.vehiclePlate].value)
      const vehicleFormData = {
        [ids.vehicleOwnerName]: {
          ...formData[ids.vehicleOwnerName],
          value: result.nomeProprietario?.trim(),
        },
        [ids.vehicleUfPlate]: {
          ...formData[ids.vehicleUfPlate],
          value: result.ufPlaca?.trim(),
        },
        [ids.vehicleCategory]: {
          ...formData[ids.vehicleCategory],
          value: result.descricaoCategoria?.trim(),
        },
        [ids.vehicleSpecification]: {
          ...formData[ids.vehicleSpecification],
          value: result.descricaoEspecie?.trim(),
        },
        [ids.vehicleModel]: {
          ...formData[ids.vehicleModel],
          value: result.descricaoMarcaModelo?.trim(),
        },
        [ids.vehicleColor]: {
          ...formData[ids.vehicleColor],
          value: result.descricaoCor?.trim(),
        },
        [ids.vehicleType]: {
          ...formData[ids.vehicleType],
          value: result.descricaoTipo?.trim(),
        },
        [ids.vehicleAssemblyYear]: {
          ...formData[ids.vehicleAssemblyYear],
          value: result.anoFabricacao?.toString().trim(),
        },
        [ids.vehicleModelYear]: {
          ...formData[ids.vehicleModelYear],
          value: result.anoModelo?.toString().trim(),
        },
        [ids.vehicleProprietaryMunicipality]: {
          ...formData[ids.vehicleProprietaryMunicipality],
          value: result.cidadeImovelProprietario?.trim(),
        }
      };

      setFormData(prev => ({ ...prev, ...vehicleFormData }));
      setInputError(checkErrors({ formData: vehicleFormData, inputError }));
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }

    try {
      const filterParams = {
        orderby: "data_infracao",
        orderDirection: "desc",
        pageSize: 1,
        filters: {
          placa: formData[ids.vehiclePlate].value
        }
      };
      const data = await trafficViolationService.list(filterParams);
      if (data?._embedded) {
        const recentViolation = getRecentViolationId(data?._embedded?.content);
        if (recentViolation) {
          const violationData = await trafficViolationService.find(recentViolation.id);
          showToast({
            title: getMessageRecentViolation(recentViolation, violationData),
            status: "warning"
          });
        }
      }
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  }

  const setAitLocation = (value) => {
    const newFormData = {
      ...formData,
      [ids.aitLocation]: {
        ...formData[ids.aitLocation],
        value: value
      }
    }
    setFormData(newFormData)
  };

  const fetchData = async () => {
    try {
      const aitTypes = await trafficViolationTypeService.lookup();
      const aitAgents = await agentsService.lookup();
      const config = await configService.get();
      const currentLoggedUser = getUserData();
      setOnlyPlateStateRequired(config.traffic_violation_only_plate_state_required);
      setAitTypes(aitTypes.map((it) => ({ ...it, displayLabel: formatAitTypeDisplayLabel(it) })));
      setAitAgents(aitAgents.filter((a) => a.id === currentLoggedUser.agente_id));
      const initialForm = initFormData();
      initialForm[ids.aitAgent].value = currentLoggedUser.agente_id;
      setFormData(initialForm);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
      redirectToList();
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const observationsValue = formData?.[ids.aitObservations]?.value ?? "";
  useEffect(() => {
    if (observationsTextareaRef.current && pendingSelectionRef.current) {
      const { start, end } = pendingSelectionRef.current;
      observationsTextareaRef.current.setSelectionRange(start, end);
      pendingSelectionRef.current = null;
    }
  }, [observationsValue]);

  const toDTO = () => ({
    placa: formData[ids.vehiclePlate].value.trim(),
    nome_proprietario_veiculo_ait: formData[ids.vehicleOwnerName].value.trim(),
    uf_placa_veiculo: formData[ids.vehicleUfPlate].value.trim(),
    descricao_categoria_veiculo: formData[ids.vehicleCategory].value.trim(),
    descricao_especie_veiculo: formData[ids.vehicleSpecification].value.trim(),
    descricao_marca_modelo_veiculo: formData[ids.vehicleModel].value.trim(),
    descricao_cor_veiculo: formData[ids.vehicleColor].value.trim(),
    descricao_tipo_veiculo: formData[ids.vehicleType].value.trim(),
    ano_fabricacao_veiculo: formData[ids.vehicleAssemblyYear].value.trim(),
    ano_modelo_veiculo: formData[ids.vehicleModelYear].value.trim(),
    cidade_proprietario_veiculo: formData[ids.vehicleProprietaryMunicipality].value,
    id_agente: formData[ids.aitAgent].value,
    id_tipo_infracao: formData[ids.aitType].value,
    data_infracao: toServerDate(formData[ids.aitDate].value),
    horario_infracao: formData[ids.aitTime].value,
    logradouro_local_infracao: formData[ids.aitLocation].value.trim(),
    complemento_local_infracao: formData[ids.aitReference].value.trim(),
    observacoes: formData[ids.aitObservations].value.trim(),
    observacoes_videomonitoramento: "",
  });

  const toFormData = () => {
    const formValues = toDTO()

    const data = new FormData();

    Object.entries(formValues).forEach(([key, value]) => {
      data.append(key, value);
    });

    const filesObject = imagesRef?.current?.getFiles();
    if (filesObject) {
      Object.values(filesObject).forEach((img) => {
        const file = img.file;
        if (file instanceof File) {
          data.append("files", file);
        }
      });
    }

    return data;
  };

  const handleSave = async () => {
    try {
      const data = toFormData();
      await trafficViolationService.create(data);
      showToast({ title: t("traffic-violation.messages.success.create"), status: "success" });
      redirectToList();
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  }

  return (
    <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
      {/*Vehicle card*/}
      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <CardHeader mb="20px">
          <Text color={textColor} fontSize="lg" fontWeight="bold">
            {t(`${formTranslationKey}.title.vehicle-data`)}
          </Text>
        </CardHeader>

        <CardBody>
          {formData ? (
            <Stack w="100%" direction="column" spacing={cardSpacing}>
              {/*vehicle-plate*/}
              <Stack alignSelf="flex-start" spacing={cardSpacing} direction="row" minW="50%" alignItems="flex-start">
                <FormControl isRequired isInvalid={inputError[ids.vehiclePlate]}>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehiclePlate}
                    value={formData[ids.vehiclePlate].value.toUpperCase()}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.vehicle-plate.placeholder`)}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehiclePlate])}
                  </FormErrorMessage>
                </FormControl>

                <LightMode>
                  <Button
                    variant="dark"
                    fontSize="sm"
                    isDisabled={inputError[ids.vehiclePlate] || !formData[ids.vehiclePlate].value}
                    leftIcon={<SearchIcon />}
                    onClick={handleVehicleSearch}
                    minW="fit-content"
                    _hover={isFormInvalid ? {} : undefined}
                  >
                    <Text>
                      {t(`${formTranslationKey}.fields.vehicle-plate.label`)}
                    </Text>
                  </Button>
                </LightMode>
              </Stack>

              <Stack spacing={formSpacing} direction={formDirection}>
                {/*vehicle-owner-name*/}
                <FormControl isInvalid={inputError[ids.vehicleOwnerName]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-owner-name.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleOwnerName}
                    value={formData[ids.vehicleOwnerName].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleOwnerName])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-uf-plate*/}
                <FormControl isRequired isInvalid={inputError[ids.vehicleUfPlate]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-uf-plate.label`)}
                  </FormLabel>
                  <Select
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleUfPlate}
                    placeholder={t(`${formTranslationKey}.fields.vehicle-uf-plate.placeholder`)}
                    value={formData[ids.vehicleUfPlate].value.toUpperCase()}
                    onChange={handleInputChange}
                  >
                    {ufs.map((uf) => (
                      <option key={uf.value} value={uf.value}>
                        {uf.value} - {uf.label}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleUfPlate])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-category*/}
                <FormControl isInvalid={inputError[ids.vehicleCategory]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-category.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleCategory}
                    value={formData[ids.vehicleCategory].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleCategory])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-specification*/}
                <FormControl isInvalid={inputError[ids.vehicleSpecification]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-specification.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleSpecification}
                    value={formData[ids.vehicleSpecification].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleSpecification])}
                  </FormErrorMessage>
                </FormControl>
              </Stack>

              <Stack spacing={formSpacing} direction={formDirection}>
                {/*vehicle-model*/}
                <FormControl isRequired={!onlyPlateStateRequired} isInvalid={inputError[ids.vehicleModel]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-model.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleModel}
                    value={formData[ids.vehicleModel].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleModel])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-color*/}
                <FormControl isRequired={!onlyPlateStateRequired} isInvalid={inputError[ids.vehicleColor]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-color.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleColor}
                    value={formData[ids.vehicleColor].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleColor])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-type*/}
                <FormControl isInvalid={inputError[ids.vehicleType]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-type.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleType}
                    value={formData[ids.vehicleType].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleType])}
                  </FormErrorMessage>
                </FormControl>
                </Stack>
                <Stack spacing={formSpacing} direction={formDirection}>
                {/*vehicle municipality*/}
                <FormControl isInvalid={inputError[ids.vehicleType]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.municipality.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.vehicleProprietaryMunicipality}
                    value={formData[ids.vehicleProprietaryMunicipality].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.vehicleType])}
                  </FormErrorMessage>
                </FormControl>
                {/*vehicle-assembly-year*/}
                <YearMaskedInput
                  id={ids.vehicleAssemblyYear}
                  label={`${formTranslationKey}.fields.vehicle-assembly-year.label`}
                  value={formData[ids.vehicleAssemblyYear].value}
                  onChange={handleInputChange}
                  error={inputError[ids.vehicleAssemblyYear]}
                />

                {/*vehicle-model-year*/}
                <YearMaskedInput
                  id={ids.vehicleModelYear}
                  label={`${formTranslationKey}.fields.vehicle-model-year.label`}
                  value={formData[ids.vehicleModelYear].value}
                  onChange={handleInputChange}
                  error={inputError[ids.vehicleModelYear]}
                />
              </Stack>
            </Stack>
          ) : (
            <Loading h="300px" w="100%" />
          )}
        </CardBody>
      </Card>

      {/*Traffic violation card*/}
      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <CardHeader mb="20px">
          <Text color={textColor} fontSize="lg" fontWeight="bold">
            {t(`${formTranslationKey}.title.ait-data`)}
          </Text>
        </CardHeader>

        <CardBody>
          {formData && aitAgents && aitTypes ? (
            <Stack w="100%" direction="column" spacing={cardSpacing}>
              <Stack spacing={formSpacing} direction={formDirection}>
                {/*ait-agent*/}
                <FormControl isRequired isInvalid={inputError[ids.aitAgent]}>
                  <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                    {t(`${formTranslationKey}.fields.ait-agent.label`)}
                  </FormLabel>
                  <SearchSelect
                    required
                    valueField="id"
                    id={ids.aitAgent}
                    value={formData[ids.aitAgent].value}
                    onChange={handleInputChange}
                    isDisabled={!aitAgents.length}
                    placeholder={t(`${formTranslationKey}.fields.ait-agent.placeholder`)}
                    displayField={["codigo", "nome"]}
                    items={aitAgents}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.aitAgent])}
                  </FormErrorMessage>
                </FormControl>

                {/*ait-type*/}
                <FormControl isRequired isInvalid={inputError[ids.aitType]}>
                  <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                    {t(`${formTranslationKey}.fields.ait-type.label`)}
                  </FormLabel>
                  <SearchSelect
                    required
                    valueField="id"
                    id={ids.aitType}
                    value={formData[ids.aitType].value}
                    isDisabled={!aitTypes.length}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.ait-type.placeholder`)}
                    displayField="displayLabel"
                    items={aitTypes}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.aitType])}
                  </FormErrorMessage>
                </FormControl>
              </Stack>

              <Stack spacing={formSpacing} direction={formDirection}>
                {/*ait-date*/}
                <FormControl isRequired isInvalid={inputError[ids.aitDate]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.ait-date.label`)}
                  </FormLabel>

                  <DateTime
                    dateFormat={DATE_FORMAT}
                    timeFormat={false}
                    locale={LOCALE_PT_BR}
                    value={moment(formData[ids.aitDate].value)}
                    renderInput={(props) => (<Input {...props} fontSize="sm" variant="main" />)}
                    onChange={handleAitDateChange}
                  />

                  <FormErrorMessage>{t(inputError[ids.aitDate])}</FormErrorMessage>
                </FormControl>

                {/*ait-time*/}
                <FormControl isRequired isInvalid={inputError[ids.aitTime]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.ait-time.label`)}
                  </FormLabel>

                  <DateTime
                    dateFormat={false}
                    timeFormat={TIME_FORMAT}
                    locale={LOCALE_PT_BR}
                    value={moment(formData[ids.aitTime].value, TIME_FORMAT)}
                    renderInput={(props) => (<Input {...props} fontSize="sm" variant="main" />)}
                    onChange={handleAitTimeChange}
                  />

                  <FormErrorMessage>{t(inputError[ids.aitTime])}</FormErrorMessage>
                </FormControl>
              </Stack>

              {isVideoMonitoring ? (
                <Stack spacing="12px" w="100%">
                  {/*ait-location*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitLocation]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.ait-location.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.aitLocation}
                      value={formData[ids.aitLocation].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitLocation])}
                    </FormErrorMessage>
                  </FormControl>
                  <CameraPlacementsList setCameraPlacementRef={setAitLocation} />
                  {/*ait-location-reference*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitReference]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.ait-location-reference.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.aitReference}
                      value={formData[ids.aitReference].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitReference])}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
              ) : (
                <>
                  {/*ait-location*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitLocation]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.ait-location.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.aitLocation}
                      value={formData[ids.aitLocation].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitLocation])}
                    </FormErrorMessage>
                  </FormControl>
                  {/*ait-location-reference*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitReference]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.ait-location-reference.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.aitReference}
                      value={formData[ids.aitReference].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitReference])}
                    </FormErrorMessage>
                  </FormControl>
                </>
              )}

              {/*ait-observations*/}
              <FormControl isRequired isInvalid={!!inputError[ids.aitObservations]}>
                <FormLabel fontSize="xs" fontWeight="bold">
                  {t(`${formTranslationKey}.fields.ait-observations.label`)}
                </FormLabel>
                <Textarea
                  ref={observationsTextareaRef}
                  id={ids.aitObservations}
                  value={formData[ids.aitObservations].value}
                  onChange={handleInputChange}
                  rows={2}
                  fontSize="sm"
                  variant="main"
                  resize="vertical"
                />
                <FormErrorMessage>
                  {t(inputError[ids.aitObservations])}
                </FormErrorMessage>
              </FormControl>

              {/*ait-images*/}
              <TrafficViolationImagesForm
                ref={imagesRef}
                requiredFirstImage={isVideoMonitoring}
                firstImageMissing={isVideoMonitoring && !hasFirstImage}
                {...(isVideoMonitoring && { onFilesChange: handleImagesChange })}
              />

              <Stack w="100%" direction="row" justify="space-between">
                <Button w="125px" variant="no-effects" bg="gray.100" color="gray.700" fontSize="sm" fontWeight="bold" onClick={redirectToList}>
                  <Text>
                    {t("system.actions.cancel")}
                  </Text>
                </Button>

                <Button
                  w="125px"
                  variant="dark"
                  fontSize="sm"
                  isDisabled={isSaveDisabled}
                  onClick={handleSave}
                  _hover={isSaveDisabled ? {} : undefined}
                >
                  <Text>
                    {t("system.actions.save")}
                  </Text>
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Loading h="300px" w="100%" />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default TrafficViolationNew;
